import React, { useState, useEffect } from "react";
import { isoDateToMDY } from "@/utils/common";
import {
  Calendar,
  Building,
  Activity,
  Pencil,
  Plus,
  SlidersHorizontal
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useUser } from "@/store/useUser";
import { cn } from "@/lib/utils";
import type { EntryType } from "./add_entry/AddEntryModal";

const imagePath = process.env.PUBLIC_URL;

const programDisplayDetails = {
  "": {color: 'bg-grey-50', image: imagePath + `/programs/logoDD.png`, text : 'Diversion Database'},
  "select": {color: 'bg-grey-50', image: imagePath + `/programs/logoDD.png`, text : 'Diversion Database'},
  odr: {color: 'bg-green-50', image: imagePath + `/programs/odr.png`, text : 'Office of Diversion and Reentry Housing'},
  lead: {color: 'bg-yellow-50', image: imagePath + `/programs/lead.png`, text : 'Let Everyone Advance with Dignity / Law Enforcement Assisted Diversion'},
  mist: {color: 'bg-blue-50', image: imagePath + `/programs/mist.png`, text : 'Misdemeanor Incompetent to Stand Trial Diversion'},
  fist: {color: 'bg-red-50', image: imagePath + `/programs/fist.png`, text : 'Felony Incompetent to Stand Trial Program'},
  prg_wmn: {color: 'bg-purple-50', image: imagePath + `/programs/prg_wmn.png`, text : 'Maternal Health'},
} as Record<string, { color: string; image: string; text: string }>;

const availableFields = [
  { id: "odr", label: "ODR", default: true, mandatory: false },
  { id: "ssn", label: "SSN", default: true, mandatory: false },
  { id: "cii", label: "CII", default: true, mandatory: false },
  { id: "mrun", label: "MRUN", default: true, mandatory: true },
  { id: "cin", label: "CIN", default: true, mandatory: false },
  { id: "fin", label: "FIN", default: true, mandatory: false },
  { id: "champid", label: "ChampID", default: true, mandatory: true },
  { id: "acuity", label: "Acuity", default: false, mandatory: false },
  { id: "arrestDate", label: "Arrest Date", default: false, mandatory: false },
  { id: "booking", label: "Booking", default: false, mandatory: false },
  { id: "lastInitialAssessmentDate", label: "Last Initial Assessment", default: false, mandatory: false },
  { id: "lastLACAREReferralDate", label: "Last LACARE Referral", default: false, mandatory: false },
  { id: "lastLACAREScreeningDate", label: "Last LACARE Screening", default: false, mandatory: false },
  { id: "main", label: "Main", default: false, mandatory: false },
  { id: "rso", label: "RSO", default: false, mandatory: false },
  { id: "releaseDate", label: "Release Date", default: false, mandatory: false },
];

const quickAddOptions: Array<{ id: EntryType; label: string }> = [
  { id: "general_note", label: "General Note" },
  { id: "coordinated_care", label: "Coordinated Care Note" },
  { id: "calaim", label: "ECM Note" },
  { id: "key_event", label: "Key Event" },
  { id: "booking", label: "New Booking" },
  { id: "document", label: "Document" },
];

const defaultQuickAddItems: EntryType[] = [
  "booking",
  "document",
  "calaim",
  "coordinated_care",
  "general_note",
  "key_event",
];

const isQuickAddEntryType = (value: string): value is EntryType => {
  return quickAddOptions.some((option) => option.id === value);
};

interface PatientBannerProps {
  data: any;
  loading: boolean;
  selectedProgram?: any;
  onEditParticipant?: () => void;
  onAddEntry?: (entryType?: EntryType) => void;
}

interface BannerConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
  visibleFields: string[];
  onSave: (fields: string[]) => void;
}

interface QuickAddConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
  visibleQuickAddItems: EntryType[];
  onSave: (items: EntryType[]) => void;
}

interface ConfigOptionRowProps {
  id: string;
  label: string;
  checked: boolean;
  onToggle: () => void;
  description?: string;
  disabled?: boolean;
  requiredLabel?: string;
}

const ConfigOptionRow: React.FC<ConfigOptionRowProps> = ({
  id,
  label,
  checked,
  onToggle,
  description,
  disabled = false,
  requiredLabel,
}) => {
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex min-w-0 cursor-pointer items-center gap-2.5 rounded-[var(--border-radius-medium)] border px-3 py-2 transition-colors",
        checked
          ? "border-[var(--azure-primary-border-soft)] bg-[var(--azure-primary-soft)]"
          : "border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] hover:bg-[var(--surface-muted)]",
        disabled && "cursor-not-allowed opacity-75"
      )}
    >
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        className="mt-0.5"
        onCheckedChange={onToggle}
      />
      <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
        <div className="min-w-0">
          <Label
            htmlFor={id}
            className="cursor-pointer text-sm font-medium leading-tight text-[var(--neutral-gray-120)]"
          >
            {label}
          </Label>
          {description ? (
            <p className="mt-0.5 text-xs text-[var(--neutral-gray-100)]">
              {description}
            </p>
          ) : null}
        </div>
        {requiredLabel ? (
          <span className="shrink-0 rounded-[var(--border-radius-small)] bg-[var(--surface-muted)] px-1.5 py-0.5 text-xs text-[var(--neutral-gray-100)]">
            {requiredLabel}
          </span>
        ) : null}
      </div>
    </label>
  );
};

const BannerConfigDialog: React.FC<BannerConfigDialogProps> = ({ isOpen, onClose, visibleFields, onSave }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set(visibleFields));

  useEffect(() => {
    setSelected(new Set(visibleFields));
  }, [visibleFields, isOpen]);

  const handleToggle = (id: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelected(newSelected);
  };

  const handleSave = () => {
    onSave(Array.from(selected));
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0" contentWidth="520px" contentMaxWidth="520px">
        <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-4 py-3">
          <DialogTitle className="text-left">Configure Banner Fields</DialogTitle>
          <DialogDescription className="text-left text-xs text-[var(--neutral-gray-100)]">
            Choose which fields stay visible in the participant banner.
          </DialogDescription>
        </DialogHeader>
        <div className="px-4 py-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {availableFields.map((field) => (
              <ConfigOptionRow
                key={field.id}
                id={field.id}
                label={field.label}
                checked={field.mandatory || selected.has(field.id)}
                disabled={field.mandatory}
                onToggle={() => handleToggle(field.id)}
                requiredLabel={field.mandatory ? "Required" : undefined}
              />
            ))}
          </div>
        </div>
        <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-4 py-3">
          <Button size="sm" onClick={handleSave}>Save</Button>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const QuickAddConfigDialog: React.FC<QuickAddConfigDialogProps> = ({
  isOpen,
  onClose,
  visibleQuickAddItems,
  onSave,
}) => {
  const [selected, setSelected] = useState<Set<EntryType>>(new Set(visibleQuickAddItems));

  useEffect(() => {
    setSelected(new Set(visibleQuickAddItems));
  }, [isOpen, visibleQuickAddItems]);

  const handleToggle = (id: EntryType) => {
    const updated = new Set(selected);
    if (updated.has(id)) {
      updated.delete(id);
      setSelected(updated);
      return;
    }

    updated.add(id);
    setSelected(updated);
  };

  const handleSave = () => {
    const items = Array.from(selected);
    if (items.length > 0) {
      onSave(items);
      onClose();
      return;
    }

    onSave(defaultQuickAddItems);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="max-w-md p-0" contentWidth="520px" contentMaxWidth="520px">
        <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-4 py-3">
          <DialogTitle className="text-left">Configure Quick Add</DialogTitle>
          <DialogDescription className="text-left text-xs text-[var(--neutral-gray-100)]">
            Choose which actions appear in the participant banner quick-add row.
          </DialogDescription>
        </DialogHeader>
        <div className="px-4 py-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {quickAddOptions.map((option) => (
              <ConfigOptionRow
                key={option.id}
                id={`quickadd-${option.id}`}
                label={option.label}
                checked={selected.has(option.id)}
                onToggle={() => handleToggle(option.id)}
              />
            ))}
          </div>
        </div>
        <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-4 py-3">
          <Button size="sm" onClick={handleSave}>Save</Button>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PatientBanner: React.FC<PatientBannerProps> = ({ 
  data, 
  loading, 
  selectedProgram, 
  onEditParticipant,
  onAddEntry
}) => {
  const { user, updateSystemSettingsFavorites } = useUser();
  const [configOpen, setConfigOpen] = useState(false);
  const [quickAddConfigOpen, setQuickAddConfigOpen] = useState(false);
  const [visibleFields, setVisibleFields] = useState<string[]>([]);
  const [visibleQuickAddItems, setVisibleQuickAddItems] = useState<EntryType[]>(defaultQuickAddItems);

  useEffect(() => {
    // Initialize from user settings
    if (user && user.settings?.systemSettingsFavorites) {
      const savedFields = user.settings.systemSettingsFavorites
        .filter((s: string) => s.startsWith("BANNER_FIELD:"))
        .map((s: string) => s.replace("BANNER_FIELD:", ""));

      const savedQuickAddItems = user.settings.systemSettingsFavorites
        .filter((s: string) => s.startsWith("QUICK_ADD:"))
        .map((s: string) => s.replace("QUICK_ADD:", ""))
        .filter((value: string): value is EntryType => isQuickAddEntryType(value));
      
      if (savedFields.length > 0) {
        setVisibleFields(savedFields);
      } else {
        // Default fields
        setVisibleFields(availableFields.filter(f => f.default).map(f => f.id));
      }

      if (savedQuickAddItems.length > 0) {
        setVisibleQuickAddItems(savedQuickAddItems);
        return;
      }

      setVisibleQuickAddItems(defaultQuickAddItems);
      return;
    } else {
        // Default fields if no user settings
        setVisibleFields(availableFields.filter(f => f.default).map(f => f.id));
        setVisibleQuickAddItems(defaultQuickAddItems);
    }
  }, [user]);

  const handleSaveConfig = async (fields: string[]) => {
    // Ensure mandatory fields are present
    const mandatory = availableFields.filter(f => f.mandatory).map(f => f.id);
    const uniqueFields = Array.from(new Set([...fields, ...mandatory]));
    
    setVisibleFields(uniqueFields);

    if (user && updateSystemSettingsFavorites) {
      // Get existing favorites that are NOT banner fields
      const otherFavorites = (user.settings?.systemSettingsFavorites || [])
        .filter((s: string) => !s.startsWith("BANNER_FIELD:"));
      
      // Add new banner fields
      const newFavorites = [
        ...otherFavorites,
        ...uniqueFields.map(f => `BANNER_FIELD:${f}`)
      ];

      await updateSystemSettingsFavorites(newFavorites);
    }
  };

  const handleSaveQuickAdd = async (items: EntryType[]) => {
    const nextItems = items.length > 0 ? items : defaultQuickAddItems;
    setVisibleQuickAddItems(nextItems);

    if (!user || !updateSystemSettingsFavorites) {
      return;
    }

    const otherFavorites = (user.settings?.systemSettingsFavorites || [])
      .filter((s: string) => !s.startsWith("QUICK_ADD:"));

    const newFavorites = [
      ...otherFavorites,
      ...nextItems.map((item) => `QUICK_ADD:${item}`)
    ];

    await updateSystemSettingsFavorites(newFavorites);
  };

  if (loading) {
    return (
      <div className="w-full h-24 bg-white border-b border-gray-200 animate-pulse p-4 flex items-center gap-4">
        <div className="h-16 w-16 bg-gray-200 rounded-full"></div>
        <div className="space-y-2">
          <div className="h-4 w-48 bg-gray-200 rounded"></div>
          <div className="h-3 w-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const participant = data.getParticipantByODRNumber;
  
  const activeProgramDetails = selectedProgram || participant.programDetails?.find((p: any) => p.active) || participant.programDetails?.[0];
  
  const programDescription = activeProgramDetails?.programDescription || "";
  const activeProgramKey = programDescription.toLowerCase();
  
  const displayDetails = programDisplayDetails[activeProgramKey] || programDisplayDetails[""];
  const programImage = displayDetails.image;

  // Calculate Age
  const calculateAge = (dobString: string) => {
    if (!dobString) return "";
    const today = new Date();
    const dob = new Date(dobString);
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  };

  const age = calculateAge(participant.dobHistory?.[0]?.value);

  const CustomBadge = ({ label, color = "blue" }: { label: string, color?: string }) => (
    <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider bg-${color}-100 text-${color}-800 border border-${color}-200 shadow-sm`}>
      {label}
    </span>
  );

  const InfoBadge = ({ label, value }: { label: string, value: string | null }) => (
    <div className="flex items-center gap-2 text-[13px]">
      <span className="text-[11px] font-bold text-gray-400 uppercase tracking-tight">{label}:</span>
      <span className="text-[11px] font-medium text-gray-900">{value || "—"}</span>
    </div>
  );

  const getValueForField = (fieldId: string) => {
    switch (fieldId) {
      case "odr": return participant.id;
      case "ssn": return participant.ssn;
      case "cii": return participant.cii;
      case "mrun": return participant.mrun;
      case "cin": return participant.cin;
      case "fin": return participant.fin;
      case "champid": return participant.champid;
      case "acuity": return participant.acuity;
      case "arrestDate": return isoDateToMDY(participant.arrestDate);
      case "booking": return participant.booking || participant.bookingNumber;
      case "lastInitialAssessmentDate": return isoDateToMDY(participant.lastInitialAssessmentDate?.entrydate);
      case "lastLACAREReferralDate": return isoDateToMDY(participant.lastLACAREReferralDate?.entrydate);
      case "lastLACAREScreeningDate": return isoDateToMDY(participant.lastLACAREScreeningDate?.entrydate);
      case "main": return participant.mainNumber; // Mapped to mainNumber in query
      case "rso": return participant.rso;
      case "releaseDate": return isoDateToMDY(participant.releaseDate || participant.releasedDate || participant.programDetails?.[0]?.releaseDate);
      default: return null;
    }
  };

  return (
        <div data-testid="participant-banner" className="app-page-shell relative z-20 overflow-hidden shadow-none">
          {/* Settings Gear - Lower Right */}
          <Button
            variant="ghost"
            size="sm"
            className="absolute bottom-2 right-2 h-8 w-8 p-0 text-gray-400 hover:text-gray-600"
            title="Configure Banner Fields"
            onClick={() => setConfigOpen(true)}
          >
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
    
          <div className="flex gap-4 px-4 py-3">
            {/* Main Content Area */}
            <div className="flex-grow space-y-3">
                                  {/* Row 1: Name & Status & Action Buttons */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <h1 className="text-2xl font-bold text-gray-900 leading-none">
                                        {participant.name?.lastName}, {participant.name?.firstName}
                                      </h1>
                                      {participant.genderHistory?.[0]?.value && (
                                        <CustomBadge label={participant.genderHistory?.[0]?.value} color="gray" />
                                      )}
                                      <CustomBadge label={activeProgramDetails?.active ? "Active" : "Inactive"} color={activeProgramDetails?.active ? "green" : "gray"} />
                                      {onEditParticipant && (
                                        <Button 
                                          data-testid="participant-edit-button"
                                          variant="ghost" 
                                          size="sm" 
                                          className="ml-1 h-7 gap-1.5 px-2 text-[var(--azure-primary)] hover:bg-[var(--azure-primary-soft)] hover:text-[var(--azure-primary-hover)]"
                                          title="Edit Participant"
                                          onClick={onEditParticipant}
                                        >
                                          <Pencil className="h-3.5 w-3.5" />
                                          <span className="text-xs font-medium">Edit</span>
                                        </Button>
                                      )}
                                    </div>
                                    
                                    {/* Top Right Action - Add Entry */}
                                    {onAddEntry && (
                                      <div className="ml-4 min-w-0 flex-1">
                                        <div className="ml-auto flex h-8 max-w-fit items-center gap-2 rounded-md border border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] px-2 shadow-sm">
                                          <span className="shrink-0 text-xs font-semibold uppercase text-gray-500">
                                            Quick Add
                                          </span>
                                          <div className="flex min-w-0 flex-nowrap items-center gap-1.5 overflow-x-auto">
                                            {visibleQuickAddItems.map((entryType) => {
                                              const option = quickAddOptions.find((item) => item.id === entryType);
                                              if (!option) return null;
                                              return (
                                            <Button
                                                  key={entryType}
                                                  data-testid={`participant-quick-add-${entryType}`}
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-6 gap-1 px-2 text-[11px]"
                                                  onClick={() => onAddEntry(entryType)}
                                                >
                                                  <Plus className="h-2.5 w-2.5" />
                                                  {option.label}
                                                </Button>
                                              );
                                            })}
                                          </div>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 shrink-0 p-0 text-gray-400 hover:text-gray-600"
                                            onClick={() => setQuickAddConfigOpen(true)}
                                            title="Configure quick add items"
                                            aria-label="Configure quick add items"
                                          >
                                            <SlidersHorizontal className="h-3.5 w-3.5" />
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  </div>          {/* Row 2: Identifiers (Configurable) */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-2 text-[13px]" title="Date of Birth">
              <Calendar className="h-4 w-4 text-gray-400" />
              <span className="font-medium text-gray-900">{isoDateToMDY(participant.dobHistory?.[0]?.value)}</span>
              {age !== "" && <span className="text-gray-500">({age} yrs)</span>}
            </div>
            
            {visibleFields.map((fieldId, index) => {
                const field = availableFields.find(f => f.id === fieldId);
                if (!field) return null;
                const value = getValueForField(fieldId);
                return (
                    <React.Fragment key={fieldId}>
                         <div className="h-4 w-px bg-gray-300 hidden sm:block"></div>
                         <InfoBadge label={field.label} value={value} />
                    </React.Fragment>
                );
            })}
          </div>

          {/* Row 3: Active Program Details and Clinical */}
          {activeProgramDetails && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-1 border-t border-gray-100">
               <div className="flex items-center gap-2 text-blue-700">
                  <Building className="h-4 w-4" />
                  <span className="font-bold text-sm">{activeProgramDetails.programDescription?.toUpperCase() || "UNKNOWN"}</span>
               </div>
               
               <div className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500 font-medium uppercase">Entry:</span>
                  <span className="font-semibold text-gray-800">{isoDateToMDY(activeProgramDetails.entryDate) || "—"}</span>
               </div>

               <div className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500 font-medium uppercase">Exit:</span>
                  <span className="font-semibold text-gray-800">{isoDateToMDY(activeProgramDetails.exitDate) || "N/A"}</span>
               </div>

               <div className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500 font-medium uppercase">Disposition:</span>
                  <span className="font-semibold text-gray-800">{activeProgramDetails.disposition || "—"}</span>
               </div>

               <div className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500 font-medium uppercase">Provider:</span>
                  <span className="font-semibold text-gray-800">{activeProgramDetails.provider || "—"}</span>
               </div>

               <div className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500 font-medium uppercase">Pathway:</span>
                  <span className="font-semibold text-gray-800">{activeProgramDetails.pathway || "—"}</span>
               </div>

               {participant.healthplan?.healthplan && (
                <div className="flex items-center gap-2 text-xs">
                    <Activity className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-gray-500 font-medium uppercase">Health Plan:</span>
                    <span className="font-medium text-gray-900">{participant.healthplan.healthplan}</span>
                    {participant.healthplan?.entrydate && (
                      <>
                        <span className="text-gray-300">|</span>
                        <span className="text-gray-500 font-medium uppercase">Selected:</span>
                        <span className="font-medium text-gray-900">{isoDateToMDY(participant.healthplan.entrydate)}</span>
                      </>
                    )}
                </div>
             )}
             {participant.probation && (
                <div className="flex items-center gap-2 text-xs">
                    <span className="text-gray-500 font-medium uppercase">Probation:</span>
                    <span className="font-medium text-gray-900">{participant.probation}</span>
                </div>
             )}
            </div>
          )}
        </div>
      </div>
      
      <BannerConfigDialog 
        isOpen={configOpen} 
        onClose={() => setConfigOpen(false)} 
        visibleFields={visibleFields} 
        onSave={handleSaveConfig} 
      />
      <QuickAddConfigDialog
        isOpen={quickAddConfigOpen}
        onClose={() => setQuickAddConfigOpen(false)}
        visibleQuickAddItems={visibleQuickAddItems}
        onSave={handleSaveQuickAdd}
      />
    </div>
  );
};
